import { AccountDetail } from "@humanex/interfaces";
import { getErrorMessage, toast } from "@humanex/ui/libs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { accountApiClient, referralApiClient } from "@/libs/api";
import { useAuthStore } from "@/stores/auth.store";

import { profileKeys } from "./use-profile-query";

export const accountKeys = {
  parent: ["account", "parent"] as const
};

// Query keys for consistent cache management
export const referralKeys = {
  all: ["referral"] as const,
  stats: () => [...referralKeys.all, "stats"] as const
};

interface UseReferralStatsOptions {
  enabled?: boolean;
}

export function useReferralStats(options: UseReferralStatsOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: referralKeys.stats(),
    queryFn: async () => {
      return referralApiClient.getStats();
    },
    enabled,
    staleTime: 10 * 60 * 1000 // 10 minutes for referral stats
  });
}

// Utility hook for invalidating referral cache
export function useReferralInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: referralKeys.all }),
    invalidateStats: () => queryClient.invalidateQueries({ queryKey: referralKeys.stats() })
  };
}

export function useVerifyReferralCode(options: { onSuccess?: (code: string) => void } = {}) {
  return useMutation({
    mutationFn: (code: string) => referralApiClient.verifyCode(code).then(() => code),
    onSuccess: (code) => {
      options.onSuccess?.(code);
    },
    onError: () => {
      console.log("Invalid referral code");
    }
  });
}

export function useVerifyCode(options: { onSuccess?: (result: AccountDetail) => void } = {}) {
  return useMutation({
    mutationFn: (code: string) => referralApiClient.verifyCode(code),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    }
  });
}

export function useParentAccount(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: accountKeys.parent,
    queryFn: () => accountApiClient.getParent(),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000
  });
}

export function useSetReferral(options: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();
  const loadProfile = useAuthStore((state) => state.loadProfile);

  return useMutation({
    mutationFn: (code: string) => accountApiClient.setReferral(code),
    onSuccess: async () => {
      await loadProfile();
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.parent });
      options.onSuccess?.();
      toast.success("Liên kết cộng tác viên thành công");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || "Mã giới thiệu không hợp lệ");
    }
  });
}
